import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, RefreshCw, Save, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { LensCapture } from "@/components/LensCapture";
import { useI18n } from "@/context/I18nContext";

export default function StudyLens() {
  const { t } = useI18n();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState(null);
  const [session, setSession] = useState(null);     // {passage_summary, questions}
  const [answers, setAnswers] = useState({});        // qid -> value
  const [revealed, setRevealed] = useState({});      // qid -> true

  // If arriving from Text Lens "Quiz Me", reuse the already-extracted passage (no re-scan).
  useEffect(() => {
    const incoming = location.state?.text;
    if (incoming) { setText(incoming); generate(incoming); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCapture = async (b64) => {
    setLoading(true); setText(null); setSession(null);
    try {
      const r = await api.post("/lens/extract", { image: b64 });
      if (!r.data.readable) { toast.error(r.data.note || "Couldn't read that — retake."); setLoading(false); return; }
      setText(r.data.text);
      await generate(r.data.text);
    } catch (e) {
      if (e?.response?.status === 402) toast.error("FluentLens is a premium feature.");
      else toast.error("Could not read that image.");
      setLoading(false);
    }
  };

  const generate = async (passage) => {
    setLoading(true); setSession(null); setAnswers({}); setRevealed({});
    try {
      const r = await api.post("/lens/study/generate", { text: passage });
      setSession(r.data);
    } catch { toast.error("Couldn't build a study session."); }
    finally { setLoading(false); }
  };

  const check = (q) => setRevealed((r) => ({ ...r, [q.id]: true }));
  const isCorrect = (q) => {
    const a = (answers[q.id] || "").trim().toLowerCase();
    const want = (q.answer || "").trim().toLowerCase();
    return a && want && (a === want || want.includes(a) || a.includes(want));
  };

  const saveScan = async () => {
    try {
      await api.post("/lens/scans", { type: "study", extracted_text: text, study_questions: session.questions, completed: Object.keys(revealed).length >= (session.questions || []).length });
      toast.success("Saved to FluentLens Archive");
    } catch { toast.error("Couldn't save scan."); }
  };

  return (
    <AppLayout>
      <Seo title="Study Lens · FluentLens" />
      <div className="max-w-3xl mx-auto" data-testid="study-lens">
        <Link to="/lens" className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 mb-3"><ArrowLeft className="w-4 h-4" /> FluentLens</Link>
        <h1 className="font-cabinet font-extrabold text-3xl mb-1">{t("lens.studyLens")}</h1>
        <p className="text-gray-600 text-sm mb-5">{t("lensDesc.subStudy")}</p>

        {!text && !loading && <LensCapture onCapture={onCapture} loading={loading} hint="Point at a passage to study" />}
        {loading && !session && <div className="nb-card p-10 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto" /><p className="font-cabinet font-bold mt-3">Building your study session…</p></div>}

        {session && (
          <div className="space-y-4" data-testid="study-session">
            {session.passage_summary && <div className="nb-card bg-sky/40 p-4"><div className="text-xs font-bold uppercase text-gray-500">Passage</div><p className="mt-1 text-sm">{session.passage_summary}</p></div>}
            {(session.questions || []).map((q, qi) => {
              const opts = q.options || [];
              const done = revealed[q.id];
              return (
                <div key={q.id || qi} className="nb-card p-4" data-testid={`study-q-${qi}`}>
                  <div className="flex items-center justify-between mb-2"><span className="nb-tag bg-lavender text-xs">{(q.type || "question").replace(/_/g, " ")}</span><span className="text-xs text-gray-400">{qi + 1}/{session.questions.length}</span></div>
                  <p className="font-bold" dir="auto">{q.prompt}</p>
                  {opts.length > 0 ? (
                    <div className="grid gap-2 mt-3">
                      {opts.map((o, oi) => (
                        <button key={oi} onClick={() => setAnswers((a) => ({ ...a, [q.id]: o }))} disabled={done}
                          className={`text-left border-2 border-ink rounded-xl px-3 py-2 text-sm ${answers[q.id] === o ? "bg-butter" : "bg-white"}`} data-testid={`study-opt-${qi}-${oi}`} dir="auto">{o}</button>
                      ))}
                    </div>
                  ) : (
                    <textarea value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} disabled={done}
                      placeholder="Your answer…" className="nb-input w-full mt-3 h-20" data-testid={`study-input-${qi}`} />
                  )}
                  {!done ? (
                    <button onClick={() => check(q)} className="nb-btn-primary py-2 px-4 text-sm mt-3" data-testid={`study-check-${qi}`}>Check</button>
                  ) : (
                    <div className={`mt-3 border-2 border-ink rounded-xl p-3 text-sm ${opts.length > 0 ? (isCorrect(q) ? "bg-mint/50" : "bg-peach/40") : "bg-mint/30"}`} data-testid={`study-feedback-${qi}`}>
                      {opts.length > 0 && <div className="font-bold flex items-center gap-1">{isCorrect(q) ? <><Check className="w-4 h-4" /> Nice work!</> : <>Good try — let's look again</>}</div>}
                      {q.answer && <div className="mt-1"><b>Suggested answer:</b> <span dir="auto">{q.answer}</span></div>}
                      {q.explanation && <div className="text-gray-600 mt-1" dir="auto">{q.explanation}</div>}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex gap-2">
              <button onClick={() => { setText(null); setSession(null); }} className="nb-btn-secondary flex-1 py-3"><RefreshCw className="w-4 h-4" /> New scan</button>
              <button onClick={saveScan} className="nb-btn-outline flex-1 py-3" data-testid="study-save-scan"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
